import React, { useState, useEffect, useCallback, useMemo } from "react";
import { EditableLeafletMap } from "../map";
import FloatingFeatureList from "./FloatingFeatureList";
import FloatingEditor from "./FloatingEditor";
import ProposalPreview from "./ProposalPreview";
import TopToolbar from "./TopToolbar";

interface ModificationItem {
  id?: number;
  targetFile: string;
  targetFeatureId: string;
  fieldPath: string;
  oldValue: any;
  newValue: any;
  operationType: "create" | "update" | "delete";
}

interface Proposal {
  id?: number;
  imdfArchiveId: string;
  proposalTitle?: string;
  proposalDescription?: string;
  status: "draft" | "pending_review" | "approved" | "rejected";
  modificationItems?: ModificationItem[];
}

interface FloorLevel {
  id: string;
  name: string;
  ordinal: number;
}

interface ProposalEditorProps {
  imdfFileId: string;
  geoJsonData: GeoJSON.FeatureCollection;
  onSave?: (proposal: Proposal) => void;
  onSubmit?: (proposalId: number) => void;
  onCancel?: () => void;
  readonly?: boolean;
}

/**
 * 輔助函數：獲取 feature 的類型
 */
function getFeatureType(feature: GeoJSON.Feature): string {
  const fileType = feature.properties?._file_type;
  const sourceFile = feature.properties?._sourceFile;

  if (fileType) return fileType;
  if (sourceFile) {
    // 從檔案路徑推斷類型，只取檔案名部分
    const fileName = sourceFile.split("/").pop() || sourceFile;
    return fileName.replace(".geojson", "").toLowerCase();
  }
  return "unknown";
}

/**
 * 輔助函數：根據 feature 找到其所屬樓層 ID
 */
function getLevelIdForFeature(
  feature: GeoJSON.Feature,
  allFeatures: GeoJSON.Feature[]
): string | null {
  const type = getFeatureType(feature);

  if (type === "amenity") {
    const unitIds = feature.properties?.unit_ids;
    if (Array.isArray(unitIds) && unitIds.length > 0) {
      const unit = allFeatures.find(
        (f) =>
          getFeatureType(f) === "unit" &&
          (f.id || f.properties?.id) === unitIds[0]
      );
      return unit?.properties?.level_id || null;
    }
  } else if (type === "occupant") {
    const anchorId = feature.properties?.anchor_id;
    if (anchorId) {
      const anchor = allFeatures.find(
        (f) =>
          getFeatureType(f) === "anchor" &&
          (f.id || f.properties?.id) === anchorId
      );
      if (anchor) {
        const unitId = anchor.properties?.unit_id;
        if (unitId) {
          const unit = allFeatures.find(
            (f) =>
              getFeatureType(f) === "unit" &&
              (f.id || f.properties?.id) === unitId
          );
          return unit?.properties?.level_id || null;
        }
      }
    }
  }

  return null;
}

const ProposalEditor: React.FC<ProposalEditorProps> = ({
  imdfFileId,
  geoJsonData,
  onSave,
  onSubmit,
  onCancel,
  readonly = false,
}) => {
  // 面板顯示狀態
  const [showFeatureList, setShowFeatureList] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // 樓層篩選
  const [currentFloorFilter, setCurrentFloorFilter] = useState<string | null>(null);
  const [availableFloors, setAvailableFloors] = useState<FloorLevel[]>([]);

  // 提案相關狀態
  const [selectedGeoJsonFeature, setSelectedGeoJsonFeature] = useState<GeoJSON.Feature | null>(null);
  const [proposal, setProposal] = useState<Proposal>({
    imdfArchiveId: imdfFileId,
    status: "draft",
    modificationItems: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, ModificationItem>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  // 載入草稿提案
  const loadDraftProposal = useCallback(async () => {
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com";
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/proposals/draft`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imdfArchiveId: imdfFileId,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setProposal(result.data);
      }
    } catch (err) {
      console.error("Failed to load draft proposal:", err);
    } finally {
      setIsLoading(false);
    }
  }, [imdfFileId]);

  // 載入現有草稿提案（如果存在）
  useEffect(() => {
    loadDraftProposal();
  }, [loadDraftProposal]);

  // 提取樓層資訊
  useEffect(() => {
    const levelFeatures = geoJsonData.features.filter(
      (f) => getFeatureType(f) === "level"
    );

    const floors: FloorLevel[] = levelFeatures
      .map((level) => {
        const levelId = level.id || level.properties?.id;
        let levelName =
          level.properties?.name ||
          level.properties?.short_name ||
          levelId ||
          "Unknown";

        // 如果 levelName 是物件（多語言），提取字串
        if (typeof levelName === "object" && levelName !== null) {
          levelName =
            levelName.en ||
            levelName.zh ||
            levelName["zh-Hant"] ||
            levelName["zh-TW"] ||
            Object.values(levelName)[0] ||
            "Unknown";
        }

        return {
          id: String(levelId),
          name: String(levelName),
          ordinal: level.properties?.ordinal ?? 0,
        };
      })
      .sort((a, b) => a.ordinal - b.ordinal);

    setAvailableFloors(floors);
  }, [geoJsonData]);

  // 過濾地圖顯示的 features (根據樓層)
  const filteredMapData = useMemo((): GeoJSON.FeatureCollection => {
    // 統計資訊
    const allAmenities = geoJsonData.features.filter((f) => getFeatureType(f) === "amenity");
    const allOccupants = geoJsonData.features.filter((f) => getFeatureType(f) === "occupant");

    console.log("📊 IMDF 資料統計:", {
      總features: geoJsonData.features.length,
      amenity數量: allAmenities.length,
      occupant數量: allOccupants.length,
      當前樓層篩選: currentFloorFilter || "無(顯示全部)",
    });

    if (!currentFloorFilter) {
      // 顯示所有樓層的 amenity/occupant
      const filtered = geoJsonData.features.filter((f) => {
        const type = getFeatureType(f);
        return type === "amenity" || type === "occupant";
      });

      console.log("✅ 未選樓層，顯示全部:", {
        amenity: filtered.filter(f => getFeatureType(f) === "amenity").length,
        occupant: filtered.filter(f => getFeatureType(f) === "occupant").length,
      });

      return {
        type: "FeatureCollection",
        features: filtered,
      };
    }

    // 過濾特定樓層的 amenity/occupant
    const filtered = geoJsonData.features.filter((feature) => {
      const type = getFeatureType(feature);
      if (type !== "amenity" && type !== "occupant") return false;

      const levelId = getLevelIdForFeature(feature, geoJsonData.features);
      return levelId === currentFloorFilter;
    });

    console.log(`🏢 樓層 ${currentFloorFilter} 的 features:`, {
      amenity: filtered.filter(f => getFeatureType(f) === "amenity").length,
      occupant: filtered.filter(f => getFeatureType(f) === "occupant").length,
    });

    return {
      type: "FeatureCollection",
      features: filtered,
    };
  }, [geoJsonData, currentFloorFilter]);

  /**
   * 輔助函數：為 occupant 添加 geometry (使用其 anchor 的座標)
   */
  const enrichOccupantWithGeometry = useCallback((occupant: GeoJSON.Feature): GeoJSON.Feature => {
    // 如果 occupant 已經有 geometry，直接返回
    if (occupant.geometry) {
      return occupant;
    }

    // 找到對應的 anchor
    const anchorId = occupant.properties?.anchor_id;
    if (!anchorId) {
      console.warn("Occupant 沒有 anchor_id:", occupant.id || occupant.properties?.id);
      return occupant;
    }

    const anchor = geoJsonData.features.find(
      (f) =>
        getFeatureType(f) === "anchor" &&
        (f.id || f.properties?.id) === anchorId
    );

    if (!anchor?.geometry) {
      console.warn("找不到 anchor 或 anchor 沒有 geometry:", anchorId);
      return occupant;
    }

    // 使用 anchor 的 geometry，但保留 occupant 的所有屬性
    return {
      ...occupant,
      geometry: anchor.geometry,
    };
  }, [geoJsonData]);

  // 創建用於地圖顯示的資料 (包含 level、unit geometries 和 amenity/occupant)
  const createDisplayMapData = useCallback((): GeoJSON.FeatureCollection => {
    const displayFeatures: GeoJSON.Feature[] = [];

    // 1. 如果有選擇特定樓層，加入該樓層的 level geometry
    if (currentFloorFilter) {
      const levelFeature = geoJsonData.features.find(
        (f) =>
          getFeatureType(f) === "level" &&
          (f.id || f.properties?.id) === currentFloorFilter
      );
      if (levelFeature) {
        displayFeatures.push({
          ...levelFeature,
          properties: {
            ...levelFeature.properties,
            _file_type: "level",
            _is_context: true, // 標記為背景圖層
          },
        });
      }

      // 2. 加入該樓層所有相關的 unit geometries
      const unitsOnFloor = geoJsonData.features.filter(
        (f) =>
          getFeatureType(f) === "unit" &&
          f.properties?.level_id === currentFloorFilter
      );

      if (selectedGeoJsonFeature) {
        // 有選中 feature：找出它關聯的 unit IDs
        const featureType = getFeatureType(selectedGeoJsonFeature);
        let selectedUnitIds: string[] = [];

        if (featureType === "amenity") {
          const unitIds = selectedGeoJsonFeature.properties?.unit_ids;
          if (Array.isArray(unitIds)) {
            selectedUnitIds = unitIds;
          }
        } else if (featureType === "occupant") {
          // occupant → anchor → unit
          const anchorId = selectedGeoJsonFeature.properties?.anchor_id;
          if (anchorId) {
            const anchor = geoJsonData.features.find(
              (f) =>
                getFeatureType(f) === "anchor" &&
                (f.id || f.properties?.id) === anchorId
            );
            if (anchor?.properties?.unit_id) {
              selectedUnitIds = [anchor.properties.unit_id];
            }
          }
        }

        // 將 units 分為相關和非相關，設定不同的透明度
        unitsOnFloor.forEach((unit) => {
          const unitId = unit.id || unit.properties?.id;
          const isRelated = selectedUnitIds.includes(unitId);

          displayFeatures.push({
            ...unit,
            properties: {
              ...unit.properties,
              _file_type: "unit",
              _is_context: true,
              _is_dimmed: !isRelated, // 非相關的 unit 要變淡
            },
          });
        });
      } else {
        // 未選中 feature：所有 units 正常顯示
        unitsOnFloor.forEach((unit) => {
          displayFeatures.push({
            ...unit,
            properties: {
              ...unit.properties,
              _file_type: "unit",
              _is_context: true,
            },
          });
        });
      }
    }

    // 3. 加入 amenity/occupant features
    if (!selectedGeoJsonFeature) {
      // 未選中時：顯示過濾後的 amenity/occupant
      // 對於 occupant，需要添加 geometry
      const enrichedFeatures = filteredMapData.features.map((feature) => {
        const type = getFeatureType(feature);
        if (type === "occupant") {
          return enrichOccupantWithGeometry(feature);
        }
        return feature;
      });
      displayFeatures.push(...enrichedFeatures);
    } else {
      // 選中時：顯示該 feature + 其關聯的 anchor
      const featureType = getFeatureType(selectedGeoJsonFeature);

      // 如果是 occupant，先添加 geometry
      if (featureType === "occupant") {
        displayFeatures.push(enrichOccupantWithGeometry(selectedGeoJsonFeature));

        // 也顯示對應的 anchor（可選）
        const anchorId = selectedGeoJsonFeature.properties?.anchor_id;
        if (anchorId) {
          const relatedAnchor = geoJsonData.features.find(
            (f) =>
              getFeatureType(f) === "anchor" &&
              (f.id || f.properties?.id) === anchorId
          );
          if (relatedAnchor) {
            displayFeatures.push({
              ...relatedAnchor,
              properties: {
                ...relatedAnchor.properties,
                _file_type: "anchor",
              },
            });
          }
        }
      } else {
        displayFeatures.push(selectedGeoJsonFeature);
      }
    }

    // 4. 確保每個 feature 都有正確的 _file_type 屬性
    const processedFeatures: GeoJSON.Feature[] = displayFeatures.map(
      (feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          _file_type: feature.properties?._file_type || getFeatureType(feature),
        },
      })
    );

    return {
      type: "FeatureCollection",
      features: processedFeatures,
    };
  }, [selectedGeoJsonFeature, filteredMapData, geoJsonData, currentFloorFilter, enrichOccupantWithGeometry]);

  // 處理屬性變更
  const handlePropertyChange = (fieldPath: string, newValue: any) => {
    if (!selectedGeoJsonFeature || readonly) return;

    const feature = selectedGeoJsonFeature;
    let targetFile = feature.properties?._sourceFile;
    if (!targetFile) {
      const fileType = feature.properties?._file_type;
      if (fileType === "amenity" || fileType === "occupant") {
        targetFile = `${fileType}.geojson`;
      } else {
        targetFile = "amenity.geojson";
      }
    }

    const targetFeatureId = feature.id || feature.properties?.id;

    if (!targetFeatureId) {
      setError("Selected feature has no ID");
      return;
    }

    // 取得當前值（從原始數據）
    const pathParts = fieldPath.split(".");
    let currentValue: any = feature;
    for (const part of pathParts) {
      currentValue = currentValue?.[part];
    }

    const modificationItem: ModificationItem = {
      targetFile,
      targetFeatureId,
      fieldPath,
      oldValue: currentValue,
      newValue,
      operationType: currentValue === undefined ? "create" : "update",
    };

    // 暫存到本地
    const changeKey = `${targetFeatureId}_${fieldPath}`;
    setPendingChanges((prev) => {
      const newChanges = new Map(prev);
      newChanges.set(changeKey, modificationItem);
      return newChanges;
    });
  };

  // 處理屬性刪除
  const handlePropertyDelete = (fieldPath: string) => {
    if (!selectedGeoJsonFeature || readonly) return;

    const feature = selectedGeoJsonFeature;
    let targetFile = feature.properties?._sourceFile;
    if (!targetFile) {
      const fileType = feature.properties?._file_type;
      if (fileType === "amenity" || fileType === "occupant") {
        targetFile = `${fileType}.geojson`;
      } else {
        targetFile = "amenity.geojson";
      }
    }

    const targetFeatureId = feature.id || feature.properties?.id;

    if (!targetFeatureId) {
      setError("Selected feature has no ID");
      return;
    }

    // 取得當前值
    const pathParts = fieldPath.split(".");
    let currentValue: any = feature;
    for (const part of pathParts) {
      currentValue = currentValue?.[part];
    }

    const modificationItem: ModificationItem = {
      targetFile,
      targetFeatureId,
      fieldPath,
      oldValue: currentValue,
      newValue: null,
      operationType: "delete",
    };

    // 暫存到本地
    const changeKey = `${targetFeatureId}_${fieldPath}`;
    setPendingChanges((prev) => {
      const newChanges = new Map(prev);
      newChanges.set(changeKey, modificationItem);
      return newChanges;
    });
  };

  // 批量保存待處理的更改
  const savePendingChanges = async () => {
    if (pendingChanges.size === 0) return;

    setIsSaving(true);
    setError(null);

    try {
      // 將所有待處理的更改逐一保存
      for (const item of pendingChanges.values()) {
        await saveModificationItem(item);
      }

      // 清除待處理更改
      setPendingChanges(new Map());

      // 重新載入提案以獲取最新狀態
      await loadDraftProposal();
    } catch (err) {
      setError("Failed to save changes");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // 保存單個修改項目
  const saveModificationItem = async (item: ModificationItem) => {
    if (!proposal.id) {
      setError("No active proposal");
      return;
    }

    const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com";
    const token = localStorage.getItem("token");

    const response = await fetch(`${API_URL}/proposals/${proposal.id}/items`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = "Failed to save modification item";
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch {
        errorMessage = `${errorMessage} (${response.status}): ${errorText}`;
      }
      throw new Error(errorMessage);
    }

    // 重新載入提案以更新狀態
    await loadDraftProposal();
  };

  // 提交提案供審核
  const handleSubmitProposal = async () => {
    if (!proposal.id || readonly) return;

    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com";
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/proposals/${proposal.id}/submit`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        setProposal(result.data);
        onSubmit?.(proposal.id);
      } else {
        const errorText = await response.text();
        let errorMessage = "Failed to submit proposal";
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = `Server error (${response.status}): ${errorText}`;
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 處理儲存草稿
  const handleSaveProposal = () => {
    onSave?.(proposal);
  };

  // 處理關閉編輯器
  const handleClose = () => {
    // 如果有未保存的更改,顯示確認對話框
    if (pendingChanges.size > 0) {
      const confirmed = window.confirm(
        `您有 ${pendingChanges.size} 個未保存的更改。\n確定要關閉編輯器嗎？未保存的更改將會遺失。`
      );
      if (!confirmed) return;
    }

    onCancel?.();
  };

  // 處理從列表選擇 feature
  const handleFeatureListSelect = (feature: GeoJSON.Feature) => {
    setSelectedGeoJsonFeature(feature);
    setShowEditor(true); // 開啟編輯面板
    // 可選:自動關閉列表以最大化地圖空間
    // setShowFeatureList(false);
  };

  // 處理地圖點擊 feature
  const handleMapFeatureClick = (selectedInfo: { feature: GeoJSON.Feature; layer: any; layerType: string } | null) => {
    if (!selectedInfo) return;

    const feature = selectedInfo.feature;
    const type = getFeatureType(feature);

    // 只允許點擊 amenity/occupant
    if (type === "amenity" || type === "occupant") {
      setSelectedGeoJsonFeature(feature);
      setShowEditor(true); // 開啟編輯面板
    } else {
      // 點擊其他類型（如 anchor、unit）時不做任何動作
      console.log(`點擊了 ${type} feature，但只有 amenity 和 occupant 可以編輯`);
    }
  };

  // 聚焦到修改項目對應的 feature
  const focusOnModificationItem = (item: ModificationItem) => {
    const feature = geoJsonData.features.find(
      (f) => (f.id || f.properties?.id) === item.targetFeatureId
    );

    if (feature) {
      setSelectedGeoJsonFeature(feature);
      setShowEditor(true);
    }
  };

  // 取得檔案名稱 (從第一個 feature 的 _sourceFile 推斷)
  // 必須在 early return 之前聲明,遵守 React Hooks 規則
  const fileName = useMemo(() => {
    const firstFeature = geoJsonData.features[0];
    if (firstFeature?.properties?._sourceFile) {
      const parts = firstFeature.properties._sourceFile.split("/");
      return parts[0] || "IMDF 檔案";
    }
    return "IMDF 檔案";
  }, [geoJsonData]);

  // Early return 必須在所有 hooks 之後
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">載入提案資料中...</span>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 relative overflow-hidden">
      {/* 頂部工具列 */}
      <TopToolbar
        fileName={fileName}
        proposalStatus={proposal.status}
        modificationCount={proposal.modificationItems?.length || 0}
        pendingChangesCount={pendingChanges.size}
        availableFloors={availableFloors}
        currentFloor={currentFloorFilter}
        onToggleList={() => setShowFeatureList(!showFeatureList)}
        onFloorChange={setCurrentFloorFilter}
        onShowPreview={() => setShowPreview(!showPreview)}
        onSaveDraft={handleSaveProposal}
        onSubmit={handleSubmitProposal}
        onClose={handleClose}
        readonly={readonly}
        submitDisabled={!proposal.modificationItems?.length || pendingChanges.size > 0}
      />

      {/* 錯誤提示 */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4 flex-shrink-0">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 text-sm mt-1"
          >
            關閉
          </button>
        </div>
      )}

      {/* 全螢幕地圖區域 */}
      <div className="flex-1 relative">
        <EditableLeafletMap
          geoJsonData={createDisplayMapData()}
          height="100%"
          className="w-full h-full"
          mode="view"
          selectedLayers={[]} // 空陣列表示顯示所有圖層
          onFeatureSelect={handleMapFeatureClick}
          enableClustering={false}
          fitBounds={true}
          showPopup={false} // 不顯示 popup 和 tooltip
        />

        {/* 修改預覽浮窗 */}
        {showPreview && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-2/3 max-h-64 bg-white rounded-lg shadow-2xl overflow-hidden z-30 border border-gray-200">
            <div className="flex justify-between items-center p-3 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-900">修改預覽</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto max-h-56">
              <ProposalPreview
                modificationItems={proposal.modificationItems || []}
                onItemClick={focusOnModificationItem}
                onItemDelete={
                  readonly
                    ? undefined
                    : async (itemId) => {
                        try {
                          const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com";
                          const token = localStorage.getItem("token");

                          const response = await fetch(`${API_URL}/proposals/items/${itemId}`, {
                            method: "DELETE",
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          });

                          if (response.ok) {
                            await loadDraftProposal(); // 重新載入
                          }
                        } catch (err) {
                          setError("Failed to delete modification item");
                        }
                      }
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* 左側浮動 Feature 列表 - 移到地圖容器外 */}
      <FloatingFeatureList
        features={geoJsonData.features}
        selectedFeature={selectedGeoJsonFeature}
        onFeatureSelect={handleFeatureListSelect}
        isOpen={showFeatureList}
        onClose={() => setShowFeatureList(false)}
      />

      {/* 右側浮動編輯器 - 移到地圖容器外 */}
      <FloatingEditor
        feature={selectedGeoJsonFeature}
        onPropertyChange={handlePropertyChange}
        onPropertyDelete={handlePropertyDelete}
        onSaveChanges={savePendingChanges}
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setSelectedGeoJsonFeature(null); // 清除選中狀態，恢復顯示所有 features
        }}
        readonly={readonly || proposal.status !== "draft"}
        isSaving={isSaving}
        pendingChangesCount={pendingChanges.size}
      />
    </div>
  );
};

export default ProposalEditor;
